import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { SharedModule } from '@Shared/shared.module';

interface Unavailability {
  from: Date;
  to: Date;
}

@Component({
  selector: 'app-calendar-view',
  templateUrl: './calendar-view.component.html',
  styleUrls: ['./calendar-view.component.scss'],
  imports: [SharedModule]
})
export class CalendarViewComponent implements OnInit, OnDestroy {
  @Input() schedules!: FormArray;

  currentMonth: number;
  currentYear: number;
  weeks: (Date | null)[][] = [];
  unavailabilityList: Unavailability[] = [];
  hoveredRange: any = null;

  private scheduleSub: Subscription | null = null;

  ngOnInit(): void {
    const today = new Date();
    this.currentMonth = today.getMonth();
    this.currentYear = today.getFullYear();
    this.generateCalendar();

    // Setup valueChanges watcher
    this.scheduleSub = this.schedules.valueChanges.subscribe(() => {
      this.extractUnavailability();
    });

    // Initial extraction
    this.extractUnavailability();
  }

  ngOnDestroy(): void {
    this.scheduleSub?.unsubscribe();
  }

  extractUnavailability(): void {
    this.unavailabilityList = this.schedules.controls
      .filter(ctrl => (ctrl as FormGroup).get('isActive')?.value !== false)
      .map(ctrl => {
        const from = new Date((ctrl as FormGroup).get('startTime')?.value);
        const to = new Date((ctrl as FormGroup).get('endTime')?.value);
        return { from, to };
      });
  }

  generateCalendar(): void {
    const firstDayOfMonth = new Date(this.currentYear, this.currentMonth, 1);
    const lastDayOfMonth = new Date(this.currentYear, this.currentMonth + 1, 0);

    const startDay = new Date(firstDayOfMonth);
    startDay.setDate(startDay.getDate() - startDay.getDay());

    const endDay = new Date(lastDayOfMonth);
    endDay.setDate(endDay.getDate() + (6 - endDay.getDay()));

    const current = new Date(startDay);
    const weeks: (Date | null)[][] = [];

    while (current <= endDay) {
      const week: (Date | null)[] = [];
      for (let i = 0; i < 7; i++) {
        week.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }
      weeks.push(week);
    }

    this.weeks = weeks;
  }

  isSameDay(d1: Date, d2: Date): boolean {
    return d1.getFullYear() === d2.getFullYear() &&
           d1.getMonth() === d2.getMonth() &&
           d1.getDate() === d2.getDate();
  }

  formatTime(date: Date): string {
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }
  
  formatDay(date: Date): string {
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  }

  getTooltipText(range: { from: Date; to: Date }): string {
    const fromDay = this.formatDay(range.from);
    const toDay = this.formatDay(range.to);
    const fromTime = this.formatTime(range.from);
    const toTime = this.formatTime(range.to);
    return `${fromDay}, ${fromTime} – ${toDay}, ${toTime}`;
  }

  getBarClass(day: Date, range: Unavailability): string {
    const isStart = this.isSameDay(day, range.from);
    const isEnd = this.isSameDay(day, range.to);
    const isSunday = day.getDay() === 0;
    const isSaturday = day.getDay() === 6;

    const classes = ['bar'];

    if (isStart && isEnd) {
      classes.push('single-day');
    } else if (isStart) {
      classes.push('start');
    } else if (isEnd) {
      classes.push('end');
    } else {
      classes.push('middle');
    }

    // Add visual rounding at week boundaries
    if (!isEnd && isSaturday && range.to > day) {
      classes.push('week-end');
    }
    if (!isStart && isSunday && range.from < day) {
      classes.push('week-start');
    }

    return classes.join(' ');
  }

  getBarText(day: Date, range: Unavailability): string | null {
    if (this.isSameDay(day, range.from)) return this.formatTime(range.from);
    if (this.isSameDay(day, range.to)) return this.formatTime(range.to);
    return null;
  }

  getUnavailabilityForDay(day: Date): Unavailability[] {
    return this.unavailabilityList.filter(range => {
      const start = new Date(range.from.getFullYear(), range.from.getMonth(), range.from.getDate());
      const end = new Date(range.to.getFullYear(), range.to.getMonth(), range.to.getDate());
      const target = new Date(day.getFullYear(), day.getMonth(), day.getDate());
      return target >= start && target <= end;
    });
  }

  prevMonth(): void {
    if (this.currentMonth === 0) {
      this.currentMonth = 11;
      this.currentYear--;
    } else {
      this.currentMonth--;
    }
    this.generateCalendar();
  }

  nextMonth(): void {
    if (this.currentMonth === 11) {
      this.currentMonth = 0;
      this.currentYear++;
    } else {
      this.currentMonth++;
    }
    this.generateCalendar();
  }

  getMonthYearLabel(): string {
    const date = new Date(this.currentYear, this.currentMonth);
    return date.toLocaleString('default', { month: 'long', year: 'numeric' });
  }
}
